import React, { useMemo, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { formatDistanceToNow } from "date-fns";
import { toast } from "react-hot-toast";
import {
  ArrowLeftRight,
  BarChart3,
  Calendar,
  CheckCircle2,
  Clock,
  Filter,
  MessageSquare,
  Plus,
  Search,
  ShieldCheck,
  Target,
  Trophy,
  UserCheck,
  Users,
  Vote,
} from "lucide-react";
import api, { API_ENDPOINTS } from "../config/api";
import { useAuth } from "../contexts/AuthContext";

const STATUS_STYLES = {
  voting: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  awaiting_resolution:
    "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  passed: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  rejected: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  resolved:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200",
};

const TYPE_STYLES = {
  platform: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-200",
  dispute: "bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-200",
};

const defaultFilters = {
  search: "",
  status: "",
  proposalType: "",
  page: 1,
  limit: 10,
};

const GovernancePage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [filters, setFilters] = useState(defaultFilters);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { data: governanceData, isLoading: isLoadingProposals, error: governanceError, refetch: refetchGovernance } = useQuery(
    ["governance", filters],
    async () => {
      try {
        const { data } = await api.get(API_ENDPOINTS.GOVERNANCE.LIST, {
          params: {
            ...filters,
            // Clean up empty string filters
            status: filters.status || undefined,
            proposalType: filters.proposalType || undefined,
            search: filters.search || undefined,
          },
        });
        console.log("Governance data received:", {
          proposalsCount: data?.proposals?.length || 0,
          total: data?.stats?.total || 0,
          filters: filters,
        });
        return data;
      } catch (error) {
        console.error("Error fetching governance data:", error);
        throw error;
      }
    },
    { 
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: 1
    }
  );

  const { data: leaderboardData } = useQuery(
    ["governance", "leaderboard", "preview"],
    async () => {
      const { data } = await api.get(API_ENDPOINTS.GOVERNANCE.LEADERBOARD, {
        params: { limit: 8, sortBy: "votesCast", sortOrder: "desc" },
      });
      return data.leaderboard || [];
    }
  );

  // Fetch voting config (voting duration and activity points)
  const { data: votingConfig } = useQuery(
    ["governance", "eligibility"],
    async () => {
      if (!user) return null;
      try {
        const { data } = await api.get(API_ENDPOINTS.GOVERNANCE.ELIGIBILITY);
        return {
          votingDurationDays: data.votingDurationDays || 5,
          activityPointsForVoting: data.activityPointsForVoting || 5
        };
      } catch (error) {
        console.error('Error fetching voting config:', error);
        return { votingDurationDays: 5, activityPointsForVoting: 5 };
      }
    },
    { enabled: !!user, refetchOnWindowFocus: false }
  );

  // Use the SAME API endpoint as chat list to fetch chats with jobs/gigs
  const { data: chatsData, isFetching: isLoadingEligibleWork, error: eligibleWorkError } = useQuery(
    ["chats", "governance-eligible"],
    async () => {
      try {
        // Use the EXACT same endpoint as chat list
        const { data } = await api.get(API_ENDPOINTS.CHATS.LIST);
        console.log("[governance] Chats data received (same as chat list):", {
          chats: data?.chats || [],
          chatsCount: data?.chats?.length || 0,
          fullData: data
        });
        return data;
      } catch (error) {
        console.error("[governance] Error fetching chats:", error);
        console.error("[governance] Error details:", error.response?.data);
        throw error;
      }
    },
    { 
      enabled: isCreateModalOpen,
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 0, // Always refetch when modal opens
      cacheTime: 0 // Don't cache to always get fresh data
    }
  );

  // Process chats data to extract jobs and orders (same as chat list would show)
  // Filter for workflowStatus: include 'deposit', 'in-progress', 'completed'
  const eligibleWork = useMemo(() => {
    if (!chatsData?.chats) return { jobs: [], gigOrders: [] };

    const chats = chatsData.chats;
    // Filter chats to only include those with eligible workflowStatus
    const eligibleChats = chats.filter(chat => {
      const workflowStatus = chat.workflowStatus;
      return workflowStatus === 'deposit' || workflowStatus === 'in-progress' || workflowStatus === 'completed';
    });

    console.log("[governance] Filtered chats by workflowStatus:", {
      totalChats: chats.length,
      eligibleChats: eligibleChats.length,
      eligibleWorkflowStatuses: ['deposit', 'in-progress', 'completed']
    });

    const jobs = [];
    const gigOrders = [];
    const orderMap = new Map(); // To deduplicate orders

    eligibleChats.forEach(chat => {
      if (chat.job && typeof chat.job === 'object' && chat.job._id) {
        // Get other participant
        const otherParticipant = chat.participants?.find(p => {
          const participantId = p.user?._id?.toString() || p.user?.toString();
          return participantId !== user?._id?.toString();
        });

        // Determine if user is client or talent (need to fetch job details to be sure)
        // For now, use participant info
        const isUserClient = chat.participants?.find(p => {
          const participantId = p.user?._id?.toString() || p.user?.toString();
          return participantId === user?._id?.toString();
        })?.role === 'client';

        const otherUsername = otherParticipant?.user?.username || 'Unknown';

        jobs.push({
          _id: chat.job._id || chat.job,
          title: chat.job.title || 'Untitled Job',
          status: chat.job.status || 'unknown',
          chatId: chat._id,
          chatWorkflowStatus: chat.workflowStatus,
          chatStatus: chat.status,
          price: chat.price,
          otherPartyUsername: otherUsername,
          userRole: isUserClient ? 'client' : 'talent',
          // Store full chat and job data for later use
          _chat: chat,
          _job: chat.job
        });
      }

      if (chat.gig && typeof chat.gig === 'object' && chat.gig._id) {
        // For gigs, we need to fetch orders separately
        // For now, show the chat itself and we'll handle orders when creating the dispute
        const otherParticipant = chat.participants?.find(p => {
          const participantId = p.user?._id?.toString() || p.user?.toString();
          return participantId !== user?._id?.toString();
        });

        const isUserClient = chat.participants?.find(p => {
          const participantId = p.user?._id?.toString() || p.user?.toString();
          return participantId === user?._id?.toString();
        })?.role === 'client';

        const otherUsername = otherParticipant?.user?.username || chat.gig?.talent?.username || 'Unknown';
        
        // For now, represent the gig chat as an "order" entry
        // When creating the dispute, we'll need to fetch the actual order from the chat
        // Use chat._id as the identifier - we'll resolve to actual order ID when submitting
        const chatKey = `chat-${chat._id}`;
        if (!orderMap.has(chatKey)) {
          orderMap.set(chatKey, {
            _id: `CHAT_${chat._id}`, // Mark as chat-based, will resolve to order when submitting
            chatId: chat._id,
            orderNumber: `CHAT-${chat._id.slice(-8).toUpperCase()}`,
            status: chat.status || 'active',
            chatWorkflowStatus: chat.workflowStatus,
            chatStatus: chat.status,
            price: chat.price,
            gig: chat.gig,
            otherPartyUsername: otherUsername,
            userRole: isUserClient ? 'client' : 'talent',
            // Store full chat and gig data
            _chat: chat,
            _gig: chat.gig,
            _isChatBased: true // Flag to indicate we need to resolve to order
          });
        }
      }
    });

    const orders = Array.from(orderMap.values());

    console.log("[governance] Processed eligible work from chats:", {
      jobsCount: jobs.length,
      ordersCount: orders.length,
      jobs,
      orders
    });

    return {
      jobs,
      gigOrders: orders
    };
  }, [chatsData, user]);

  const createProposalMutation = useMutation(
    (payload) => api.post(API_ENDPOINTS.GOVERNANCE.CREATE, payload),
    {
      onSuccess: async () => {
        // Invalidate and refetch all governance-related queries
        await queryClient.invalidateQueries(["governance"]);
        await queryClient.invalidateQueries(["governance", "leaderboard"]);
        // Force refetch with current filters
        await queryClient.refetchQueries(["governance", filters], { active: true });
        await queryClient.refetchQueries(["governance", "leaderboard", "preview"], { active: true });
        // Refresh user data to show updated activity points
        await queryClient.invalidateQueries(["auth", "me"]);
        toast.success("Proposal submitted for DAO review.");
        setIsCreateModalOpen(false);
      },
      onError: (error) => {
        console.error("Proposal creation error:", error);
        toast.error(
          error.response?.data?.error || "Unable to submit proposal right now."
        );
      },
    }
  );

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: field === "page" ? value : 1,
    }));
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
  };

  const proposals = governanceData?.proposals || [];
  const stats = governanceData?.stats;
  const pagination = governanceData?.pagination;

  // Debug logging
  useEffect(() => {
    if (governanceData) {
      console.log("Current governance state:", {
        proposalsCount: proposals.length,
        stats: stats,
        pagination: pagination,
        filters: filters,
      });
    }
  }, [governanceData, proposals.length, stats, pagination, filters]);

  // Platform proposals require 10+ activity points
  // Dispute proposals can be created by anyone involved in a job/gig (no activity point requirement)
  const canCreatePlatform = (user?.stats?.activityPoints || 0) >= 10;
  const canCreateDispute = true; // No activity point requirement for disputes
  const canCreate = canCreatePlatform || canCreateDispute; // Allow if either is true

  const statusFilters = useMemo(
    () => [
      { value: "", label: "All statuses" },
      { value: "voting", label: "Voting" },
      { value: "awaiting_resolution", label: "Awaiting resolution" },
      { value: "passed", label: "Passed" },
      { value: "rejected", label: "Rejected" },
      { value: "resolved", label: "Resolved" },
    ],
    []
  );

  const typeFilters = useMemo(
    () => [
      { value: "", label: "All proposals" },
      { value: "platform", label: "Platform upgrades" },
      { value: "dispute", label: "Dispute resolutions" },
    ],
    []
  );

  return (
    <div className="p-6 space-y-6">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-wide text-accent font-semibold mb-2">
            DAO Governance
          </p>
          <h1 className="text-3xl font-heading font-bold text-heading mb-2">
            Collective Decision Making
          </h1>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl">
            Qualified members can propose platform upgrades, resolve disputes, and
            shape the future of Workloob through transparent, on-chain inspired
            governance.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="card px-4 py-3 flex items-center space-x-3">
            <div className="rounded-full bg-purple-100 dark:bg-purple-900/40 p-2">
              <StarIcon />
            </div>
            <div>
              <p className="text-xs text-gray-500">Your activity points</p>
              <p className="text-lg font-semibold text-heading">
                {user?.stats?.activityPoints ?? 0}
              </p>
            </div>
          </div>

          <button
            className="btn-primary whitespace-nowrap"
            onClick={() => setIsCreateModalOpen(true)}
            title="You can create dispute proposals even without activity points"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Proposal
          </button>
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatsCard
          icon={<Vote className="w-5 h-5" />}
          title="Total Proposals"
          value={stats?.total ?? 0}
          accent="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200"
        />
        <StatsCard
          icon={<Clock className="w-5 h-5" />}
          title="Active Voting"
          value={stats?.byStatus?.voting ?? 0}
          accent="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200"
        />
        <StatsCard
          icon={<ShieldCheck className="w-5 h-5" />}
          title="Disputes Pending"
          value={stats?.byType?.dispute ?? 0}
          accent="bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200"
        />
        <StatsCard
          icon={<Users className="w-5 h-5" />}
          title="Eligible DAO Voters"
          value={stats?.eligibleVoters ?? 0}
          accent="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200"
        />
      </section>

      <section className="card p-4 md:p-6 space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 flex-1">
            <div>
              <label className="text-xs uppercase text-gray-500 mb-2 block">
                Search proposals
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  className="input-field pl-9"
                  placeholder="Search by title, summary, tags…"
                  value={filters.search}
                  onChange={(event) =>
                    handleFilterChange("search", event.target.value)
                  }
                />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase text-gray-500 mb-2 block">
                Proposal status
              </label>
              <select
                className="input-field"
                value={filters.status}
                onChange={(event) =>
                  handleFilterChange("status", event.target.value)
                }
              >
                {statusFilters.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs uppercase text-gray-500 mb-2 block">
                Proposal type
              </label>
              <select
                className="input-field"
                value={filters.proposalType}
                onChange={(event) =>
                  handleFilterChange("proposalType", event.target.value)
                }
              >
                {typeFilters.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={resetFilters}
                className="btn-outline flex items-center justify-center w-full"
              >
                <Filter className="w-4 h-4 mr-2" />
                Reset
              </button>
            </div>
          </div>

          <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 px-4 py-3 flex items-center gap-3 bg-gray-50 dark:bg-gray-800/40">
            <ArrowLeftRight className="w-5 h-5 text-accent" />
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Voting period lasts <span className="font-semibold">{votingConfig?.votingDurationDays || 5} days</span>.
              DAO members earn <span className="font-semibold">+{votingConfig?.activityPointsForVoting || 5} points</span> per vote.
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-3">
        {governanceError && (
          <div className="card p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <p className="text-red-700 dark:text-red-300">
              Error loading proposals: {governanceError?.response?.data?.error || governanceError?.message || "Unknown error"}
            </p>
            <button 
              onClick={() => refetchGovernance()} 
              className="btn-outline mt-3 text-sm"
            >
              Retry
            </button>
          </div>
        )}

        {isLoadingProposals && (
          <div className="card p-12 flex flex-col items-center space-y-4">
            <div className="animate-spin h-10 w-10 border-4 border-accent border-t-transparent rounded-full" />
            <p className="text-gray-500">Loading proposals...</p>
          </div>
        )}

        {!isLoadingProposals && !governanceError && proposals.length === 0 && (
          <div className="card p-12 text-center space-y-3">
            <p className="text-5xl">🏛️</p>
            <h3 className="text-xl font-semibold text-heading">
              No proposals yet in this filter
            </h3>
            <p className="text-gray-500 max-w-md mx-auto">
              Switch filters or start a new conversation by submitting a proposal
              if you meet the DAO participation requirements.
            </p>
            <div className="flex justify-center gap-3">
              <button className="btn-outline" onClick={resetFilters}>
                Reset filters
              </button>
              <button
                className="btn-primary"
                onClick={() => setIsCreateModalOpen(true)}
                title="You can create dispute proposals even without activity points"
              >
                <Plus className="w-4 h-4 mr-2" />
                New proposal
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {proposals.map((proposal) => (
            <ProposalCard key={proposal._id} proposal={proposal} />
          ))}
        </div>
      </section>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between card px-4 py-3">
          <div className="text-sm text-gray-500">
            Page {pagination.currentPage} of {pagination.totalPages}
          </div>
          <div className="flex gap-2">
            <button
              className="btn-outline text-sm"
              disabled={pagination.currentPage === 1}
              onClick={() => handleFilterChange("page", pagination.currentPage - 1)}
            >
              Previous
            </button>
            <button
              className="btn-outline text-sm"
              disabled={pagination.currentPage === pagination.totalPages}
              onClick={() => handleFilterChange("page", pagination.currentPage + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card p-6 space-y-4 lg:col-span-2">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-accent" />
            <h3 className="text-lg font-semibold text-heading">Governance stats</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MiniStat
              icon={<CheckCircle2 className="w-4 h-4" />}
              title="Platform upgrades"
              value={stats?.byType?.platform ?? 0}
              hint="Feature or policy improvements under review"
            />
            <MiniStat
              icon={<MessageSquare className="w-4 h-4" />}
              title="Disputes in queue"
              value={stats?.byType?.dispute ?? 0}
              hint="Client vs talent conflicts in the DAO pipeline"
            />
            <MiniStat
              icon={<Target className="w-4 h-4" />}
              title="Resolution rate"
              value={`${((stats?.byStatus?.resolved ?? 0) / Math.max(stats?.total ?? 1, 1) * 100).toFixed(0)}%`}
              hint="Proposals that have been resolved after voting"
            />
          </div>
          <p className="text-sm text-gray-500">
            Five-day voting windows guarantee fast but thoughtful decisions.
            Members with at least 9 activity points can vote and comment. Creating
            new proposals or disputes requires 10+ points.
          </p>
        </div>

        <div className="card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              <h3 className="text-lg font-semibold text-heading">DAO leaderboard</h3>
            </div>
            <button
              onClick={() => navigate("/governance/leaderboard")}
              className="btn-outline text-sm flex items-center gap-2"
            >
              View All
              <ArrowLeftRight className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-gray-500">
            Recognition for members who actively vote, resolve disputes, and drive
            platform improvements.
          </p>
          <div className="space-y-3 max-h-72 overflow-y-auto pr-2">
            {leaderboardData?.slice(0, 8).map((member, index) => (
              <LeaderboardRow
                key={member._id}
                position={index + 1}
                member={member}
              />
            ))}
          </div>
          <p className="text-xs text-gray-400">
            Leaderboard updates dynamically as DAO activity evolves.
          </p>
        </div>
      </section>

      <section className="card p-6 space-y-3">
        <h3 className="text-lg font-semibold text-heading flex items-center gap-2">
          <UserCheck className="w-5 h-5 text-accent" />
          Participation rules
        </h3>
        <ul className="list-disc pl-6 space-y-1 text-sm text-gray-600 dark:text-gray-400">
          <li>9+ activity points required to vote, comment, and join dispute threads.</li>
          <li>10+ activity points required to open platform proposals.</li>
          <li>Dispute proposals can be created by anyone involved in a job or gig (no activity point requirement).</li>
          <li>Each recorded vote automatically awards +5 activity points.</li>
          <li>The DAO awards the "Resolved" role to members who close out finalized proposals.</li>
        </ul>
      </section>

      {isCreateModalOpen && (
        <CreateProposalModal
          isSubmitting={createProposalMutation.isLoading}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={(payload) => createProposalMutation.mutate(payload)}
          eligibleWork={eligibleWork}
          isLoadingEligibleWork={isLoadingEligibleWork}
          eligibleWorkError={eligibleWorkError}
          canCreate={canCreate}
          canCreatePlatform={canCreatePlatform}
        />
      )}
    </div>
  );
};

const ProposalCard = ({ proposal }) => {
  const statusClass =
    STATUS_STYLES[proposal.status] || "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300";
  const typeClass =
    TYPE_STYLES[proposal.proposalType] || "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300";

  const primaryVoteOption =
    proposal.voteOptions?.reduce((top, current) =>
      current.votes > (top?.votes ?? 0) ? current : top
    , null);

  return (
    <Link to={`/governance/proposals/${proposal._id}`} className="block h-full">
      <div className="card p-5 h-full hover:shadow-lg transition-all space-y-4 flex flex-col">
        <div className="flex flex-col gap-3 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${typeClass}`}>
              {proposal.proposalType === "platform" ? "Platform" : "Dispute"}
            </span>
            <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${statusClass}`}>
              {proposal.status.replace("_", " ")}
            </span>
          </div>
          
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-heading line-clamp-2 mb-2">
              {proposal.title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
              {proposal.summary || proposal.description}
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1 text-gray-500">
                <Users className="w-3.5 h-3.5" />
                <span>{proposal.voteTallies.total} votes</span>
              </div>
              <div className="flex items-center gap-1 text-gray-500">
                <Clock className="w-3.5 h-3.5" />
                <span className="truncate max-w-[120px]">{proposal.voting.timeRemainingLabel}</span>
              </div>
            </div>

            {proposal.proposer && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <UserBadgeIcon />
                <span className="truncate">@{proposal.proposer.username}</span>
              </div>
            )}

            {proposal.tags && proposal.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {proposal.tags?.slice(0, 2).map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-xs rounded-full text-gray-600 dark:text-gray-400"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="pt-2 border-t border-gray-200 dark:border-gray-700 space-y-2">
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Participation</span>
                <span>{Math.round(proposal.analytics.participationRate ?? 0)}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-800 rounded-full h-1.5">
                <div
                  className="h-1.5 rounded-full bg-accent transition-all"
                  style={{
                    width: `${Math.min(proposal.analytics.participationRate ?? 0, 100)}%`,
                  }}
                />
              </div>
            </div>

            {primaryVoteOption && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">Leading:</span>
                <span className="font-medium text-heading">
                  {primaryVoteOption.label} ({primaryVoteOption.votes})
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

const StatsCard = ({ icon, title, value, accent }) => (
  <div className="card p-5 flex items-center justify-between">
    <div>
      <p className="text-xs uppercase text-gray-500 mb-1">{title}</p>
      <p className="text-2xl font-semibold text-heading">{value}</p>
    </div>
    <div className={`p-3 rounded-full ${accent}`}>{icon}</div>
  </div>
);

const MiniStat = ({ icon, title, value, hint }) => (
  <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-1">
    <div className="flex items-center gap-2 text-heading font-medium">
      <span className="rounded-full bg-accent/10 text-accent p-2">{icon}</span>
      {title}
    </div>
    <div className="text-2xl font-semibold text-heading">{value}</div>
    <p className="text-xs text-gray-500">{hint}</p>
  </div>
);

const LeaderboardRow = ({ position, member }) => {
  const medal = position === 1 ? "🥇" : position === 2 ? "🥈" : position === 3 ? "🥉" : position;
  return (
    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-3">
        <div className="text-lg">{medal}</div>
        <div>
          <p className="text-sm font-medium text-heading">{member.username}</p>
          <p className="text-xs text-gray-500">
            {member.votesCast} votes • {member.proposalsSubmitted} proposals
          </p>
        </div>
      </div>
      <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-200">
        {member.activityPoints} pts
      </span>
    </div>
  );
};

const CreateProposalModal = ({
  onClose,
  onSubmit,
  isSubmitting,
  eligibleWork,
  isLoadingEligibleWork,
  eligibleWorkError,
  canCreate,
  canCreatePlatform,
}) => {
  const [activeTab, setActiveTab] = useState("platform");
  const [platformForm, setPlatformForm] = useState({
    title: "",
    summary: "",
    description: "",
    problemStatement: "",
    proposedSolution: "",
    impact: "",
    implementationPlan: "",
    successMetrics: "",
    dependencies: "",
    tags: "",
  });
  const [disputeForm, setDisputeForm] = useState({
    jobModel: "",
    jobId: "",
    issueSummary: "",
    clientNarrative: "",
    talentNarrative: "",
    tags: "",
  });

  const submitPlatform = () => {
    if (!platformForm.title || !platformForm.description || platformForm.description.trim().length === 0) {
      toast.error("Provide a descriptive title and description.");
      return;
    }
    onSubmit({
      title: platformForm.title,
      summary: platformForm.summary,
      description: platformForm.description,
      proposalType: "platform",
      category: "platform",
      tags: platformForm.tags
        ? platformForm.tags.split(",").map((tag) => tag.trim()).filter(Boolean)
        : [],
      platformDetails: {
        problemStatement: platformForm.problemStatement,
        proposedSolution: platformForm.proposedSolution,
        impact: platformForm.impact,
        implementationPlan: platformForm.implementationPlan,
        successMetrics: platformForm.successMetrics,
        dependencies: platformForm.dependencies,
      },
    });
  };

  const submitDispute = async () => {
    if (!disputeForm.jobModel || !disputeForm.jobId) {
      toast.error("Select a job or gig order to dispute.");
      return;
    }
    if (!disputeForm.issueSummary || disputeForm.issueSummary.trim().length === 0) {
      toast.error("Please provide a dispute overview.");
      return;
    }

    let jobModel = disputeForm.jobModel;
    let jobId = disputeForm.jobId;

    // Get the title from the selected work item
    let workTitle = '';
    if (jobModel === 'Job') {
      const selectedJob = eligibleWork?.jobs?.find(j => j._id === jobId);
      workTitle = selectedJob?.title || 'Job';
    } else if (jobModel === 'Order') {
      const selectedOrder = eligibleWork?.gigOrders?.find(o => o._id === jobId);
      workTitle = selectedOrder?.gig?.title || 'Gig Order';
    } else if (jobModel === 'Chat') {
      const selectedChat = eligibleWork?.gigOrders?.find(o => o.chatId === jobId || o._id?.includes(jobId));
      workTitle = selectedChat?.gig?.title || 'Gig';
    }

    console.log('[governance] Submitting dispute:', {
      jobModel,
      jobId,
      workTitle,
      formData: disputeForm
    });

    onSubmit({
      title: `Dispute Resolution - ${workTitle || (jobModel === 'Chat' ? 'Gig' : jobModel)}`,
      summary: disputeForm.issueSummary,
      description: disputeForm.clientNarrative || disputeForm.talentNarrative || disputeForm.issueSummary || 'Dispute resolution request',
      proposalType: "dispute",
      category: "dispute",
      tags: disputeForm.tags
        ? disputeForm.tags.split(",").map((tag) => tag.trim()).filter(Boolean)
        : [],
      disputeContext: {
        jobModel: jobModel,
        jobId: jobId,
        issueSummary: disputeForm.issueSummary,
        clientNarrative: disputeForm.clientNarrative,
        talentNarrative: disputeForm.talentNarrative,
      },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-4xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <div>
            <h2 className="text-xl font-semibold text-heading">
              Launch a DAO proposal
            </h2>
            <p className="text-sm text-gray-500">
              Only members with 10+ activity points can open new proposals.
            </p>
          </div>
          <button className="btn-outline" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="px-6 pt-4">
          <div className="flex gap-2 mb-4">
            <button
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                activeTab === "platform"
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
              }`}
              onClick={() => setActiveTab("platform")}
            >
              Platform upgrade
            </button>
            <button
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                activeTab === "dispute"
                  ? "bg-rose-600 text-white"
                  : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
              }`}
              onClick={() => setActiveTab("dispute")}
            >
              Dispute resolution
            </button>
          </div>
        </div>

        <div className="max-h-[70vh] overflow-y-auto space-y-4 px-6 pb-6">
          {activeTab === "platform" ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  label="Proposal title"
                  value={platformForm.title}
                  onChange={(value) =>
                    setPlatformForm((prev) => ({ ...prev, title: value }))
                  }
                  placeholder="Concise title for the platform change"
                />
                <TextField
                  label="Summary"
                  value={platformForm.summary}
                  onChange={(value) =>
                    setPlatformForm((prev) => ({ ...prev, summary: value }))
                  }
                  placeholder="Short snapshot of the request"
                />
              </div>
              <TextareaField
                label="Full description"
                value={platformForm.description}
                onChange={(value) =>
                  setPlatformForm((prev) => ({
                    ...prev,
                    description: value,
                  }))
                }
                placeholder="Explain the context, what needs to change, and why it matters for the ecosystem."
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextareaField
                  label="Problem statement"
                  value={platformForm.problemStatement}
                  onChange={(value) =>
                    setPlatformForm((prev) => ({
                      ...prev,
                      problemStatement: value,
                    }))
                  }
                />
                <TextareaField
                  label="Proposed solution"
                  value={platformForm.proposedSolution}
                  onChange={(value) =>
                    setPlatformForm((prev) => ({
                      ...prev,
                      proposedSolution: value,
                    }))
                  }
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <TextareaField
                  label="Impact"
                  value={platformForm.impact}
                  onChange={(value) =>
                    setPlatformForm((prev) => ({ ...prev, impact: value }))
                  }
                />
                <TextareaField
                  label="Implementation plan"
                  value={platformForm.implementationPlan}
                  onChange={(value) =>
                    setPlatformForm((prev) => ({
                      ...prev,
                      implementationPlan: value,
                    }))
                  }
                />
                <TextareaField
                  label="Success metrics"
                  value={platformForm.successMetrics}
                  onChange={(value) =>
                    setPlatformForm((prev) => ({
                      ...prev,
                      successMetrics: value,
                    }))
                  }
                />
              </div>
              <TextField
                label="Dependencies or blockers"
                value={platformForm.dependencies}
                onChange={(value) =>
                  setPlatformForm((prev) => ({
                    ...prev,
                    dependencies: value,
                  }))
                }
                placeholder="List teams, smart contracts, or features this depends on"
              />
              <TextField
                label="Tags (comma separated)"
                value={platformForm.tags}
                onChange={(value) =>
                  setPlatformForm((prev) => ({ ...prev, tags: value }))
                }
                placeholder="platform, ui, performance"
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase text-gray-500 mb-2 block">
                    Select workload
                  </label>
                  <select
                    className="input-field"
                    value={`${disputeForm.jobModel}:${disputeForm.jobId}`}
                    onChange={(event) => {
                      const value = event.target.value;
                      console.log('[governance] Selected value:', value);
                      const [jobModel, jobId] = value.split(":");
                      console.log('[governance] Parsed:', { jobModel, jobId });
                      setDisputeForm((prev) => ({ ...prev, jobModel, jobId }));
                    }}
                  >
                    <option value="">Choose eligible job or gig order</option>
                    {isLoadingEligibleWork && (
                      <option value="" disabled>
                        Loading eligible work items...
                      </option>
                    )}
                    {eligibleWorkError && (
                      <option value="" disabled className="text-red-500">
                        Error loading work items: {eligibleWorkError?.response?.data?.error || eligibleWorkError?.message || 'Unknown error'}
                      </option>
                    )}
                    {!isLoadingEligibleWork && !eligibleWorkError && (!eligibleWork || (eligibleWork?.jobs?.length === 0 && eligibleWork?.gigOrders?.length === 0)) && (
                      <option value="" disabled>
                        No eligible jobs or gig orders — or each already has an open DAO dispute
                      </option>
                    )}
                    {eligibleWork?.jobs && eligibleWork.jobs.length > 0 && eligibleWork.jobs.map((job) => (
                      <option key={job._id} value={`Job:${job._id}`}>
                        {job.title} • {job.otherPartyUsername ? `@${job.otherPartyUsername}` : (job.userRole === 'client' ? (job.hiredTalent?.username || 'Talent') : (job.client?.username || 'Client'))}
                        {job.chatWorkflowStatus ? ` • ${job.chatWorkflowStatus}` : ''}
                      </option>
                    ))}
                    {eligibleWork?.gigOrders && eligibleWork.gigOrders.length > 0 && eligibleWork.gigOrders.map((order, idx) => {
                      // Check if this is a chat-based order (needs to resolve to actual order)
                      const isChatBased = order._id?.startsWith('CHAT_') || order._isChatBased;
                      const chatId = order.chatId || (isChatBased && order._id?.startsWith('CHAT_') ? order._id.replace('CHAT_', '') : null);
                      
                      if (!chatId && isChatBased) {
                        console.error('[governance] Chat-based order missing chatId:', order);
                      }
                      
                      const optionValue = isChatBased && chatId ? `Chat:${chatId}` : `Order:${order._id}`;
                      
                      console.log(`[governance] Gig order option ${idx + 1}:`, {
                        orderId: order._id,
                        chatId,
                        isChatBased,
                        optionValue,
                        gigTitle: order.gig?.title,
                        hasChatId: !!chatId
                      });
                      
                      return (
                        <option key={order._id || `order-${idx}`} value={optionValue}>
                          {order.gig?.title || 'Gig Order'} • {order.otherPartyUsername ? `@${order.otherPartyUsername}` : (order.userRole === 'client' ? (order.talent?.username || 'Talent') : (order.client?.username || 'Client'))}
                          {order.chatWorkflowStatus ? ` • ${order.chatWorkflowStatus}` : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <TextField
                  label="Tags (comma separated)"
                  value={disputeForm.tags}
                  onChange={(value) =>
                    setDisputeForm((prev) => ({ ...prev, tags: value }))
                  }
                  placeholder="dispute, escrow, payment"
                />
              </div>
              <TextareaField
                label="Dispute overview"
                value={disputeForm.issueSummary}
                onChange={(value) =>
                  setDisputeForm((prev) => ({
                    ...prev,
                    issueSummary: value,
                  }))
                }
                placeholder="Summarize the core issue and what needs DAO attention."
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextareaField
                  label="Client perspective"
                  value={disputeForm.clientNarrative}
                  onChange={(value) =>
                    setDisputeForm((prev) => ({
                      ...prev,
                      clientNarrative: value,
                    }))
                  }
                />
                <TextareaField
                  label="Talent perspective"
                  value={disputeForm.talentNarrative}
                  onChange={(value) =>
                    setDisputeForm((prev) => ({
                      ...prev,
                      talentNarrative: value,
                    }))
                  }
                />
              </div>
              <p className="text-xs text-gray-500">
                DAO members will review prior conversations, vote on outcomes
                (Client Refund, Talent Refund, Split Funds), and can message directly
                in the dispute room.
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40">
          <div className="text-xs text-gray-500">
            Voting eligibility: 9+ activity points. Proposal creation requires 10+
            points. Votes grant +5 activity points automatically.
          </div>
          <div className="flex gap-3">
            <button className="btn-outline" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn-primary"
              disabled={isSubmitting || (activeTab === "platform" && !canCreatePlatform)}
              onClick={activeTab === "platform" ? submitPlatform : submitDispute}
            >
              {isSubmitting ? "Submitting..." : "Submit to DAO"}
            </button>
            {activeTab === "platform" && !canCreatePlatform && (
              <p className="text-xs text-red-500 mt-2">
                Minimum 10 activity points required to create platform proposals
              </p>
            )}
            {activeTab === "dispute" && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Dispute proposals can be created by anyone involved in a job or gig
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const TextField = ({ label, value, onChange, placeholder }) => (
  <div className="space-y-1.5">
    <label className="text-xs uppercase text-gray-500">{label}</label>
    <input
      type="text"
      className="input-field"
      value={value}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
    />
  </div>
);

const TextareaField = ({ label, value, onChange, placeholder }) => (
  <div className="space-y-1.5">
    <label className="text-xs uppercase text-gray-500">{label}</label>
    <textarea
      className="input-field h-28 resize-y"
      value={value}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
    />
  </div>
);

const StarIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className="w-5 h-5 text-purple-600 dark:text-purple-300"
  >
    <path d="M11.48 3.499a.75.75 0 0 1 1.04 0l2.646 2.59 3.608.532a.75.75 0 0 1 .416 1.279l-2.607 2.542.615 3.585a.75.75 0 0 1-1.088.791L12 13.347l-3.232 1.97a.75.75 0 0 1-1.088-.79l.615-3.586-2.607-2.542a.75.75 0 0 1 .416-1.279l3.608-.532 2.646-2.59Z" />
  </svg>
);

const UserBadgeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-4 h-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.59 14.37a4.5 4.5 0 1 0-7.18 0A6 6 0 0 0 4.5 19.5h15a6 6 0 0 0-3.91-5.13Z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 11.25a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
    />
  </svg>
);

export default GovernancePage;
